/* Atlas v3 — Markets: the Universe desk. The consolidated front door for
   browsing the whole Atlas universe (~270 main + ~109 hunting names),
   answer-first in the Leverage/Global Desk idiom:

     · Top Gainer / Top Loser hero band (HeroCandles) for the chosen window
     · ranked movers LIST (default view) — logo · ticker/name · spark ·
       price · %, capped at 50 rows with a SHOW ALL expander
     · MAP view — the squarified treemap (unchanged squarify/drill/morph),
       now fed the same computed rows as the list so it respects scope
     · scope chips ALL / MAIN / GEMS (gems = hunting tier), timeframe
       dropdown 1D/5D/1M/6M/1Y + sort flip, sector filter dropdown

   This screen replaced both the old Market Map (treemap-only /markets) and
   the Explore sector-accordion homepage (/discover now lands here; the old
   screen is retained unrouted in Explore.jsx). The sector blurbs + logo-peek
   flourishes from Explore live on in the sector-filtered header.

   Data: useAtlasStocks({ includeInactive: true }) roster + useLivePrices for
   EVERY range — snapshot mode for 1D, historical mode (change1w/1m/6m/1y) for
   5D/1M/6M/1Y, for the full ~380-ticker universe. useStockUpdates (nightly,
   per RANGE_CONFIG's field map) is now only the instant-paint fallback shown
   while a range's live fetch is in flight, so the list never disagrees with
   the sheet you tap into on the SAME range — both read the live snapshot
   (fixed 2026-08-22, audit finding F3: the list used to read the nightly
   column permanently for non-1D ranges, which could differ from the sheet's
   live number by however much the stock moved since last night's close).
   /api/prices's historical mode computes its anchor closes from the OWNED
   price_history table (not per-ticker Polygon calls), which is what makes
   fetching it for the whole universe on every range switch cheap — see
   api/prices.js's fetchHistBatch. Row sparklines match the selected timeframe
   too: one batched /api/sparks?range call returns the correct windowed shape
   for every row from price_history (no row cap — fixed 2026-08-24, audit F11,
   which replaced a per-ticker useTickerBars fetch that had to be capped at
   ROW_CAP and fell back to a fixed ~10-day shape past it). 1D is the one range
   daily closes can't express intraday, so the visible 1D rows get a real
   intraday overlay from useTickerBars (bounded to on-screen rows) on top of a
   short recent-daily fallback.
   Hunting rows are passed to StockDetailProvider as extraStocks so gem taps
   open the TickerSheet (they silently no-op'd on the old Explore).

   Mobile-first: tap a row/tile → openTicker. Brings its own shell
   (TickerStrip + BottomNav). No starfield. Desktop mirror is a follow-up —
   do not let the designs drift. */
import { useMemo, useRef, useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { TickerCtx, Chip, Spark, Dropdown, SkeletonRow } from './lib'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher } from './shell'
import { HeroCandles } from './HeroCandles'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useAtlasStocks } from '@/v2/hooks/useAtlasStocks'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useStockUpdates, useSparks } from '@/v2/hooks/useSupabaseData'
import { useTickerBars } from '@/v2/hooks/useChartBars'
import { StockLogo, LOGO_MISSING } from '@/v2/components/StockLogo'
import { squarify, RANGE_CONFIG, colorForPct } from '@/v2/lib/treemap'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }

// Thin dark outline behind the white tile text so tickers + % stay legible on
// the bright green tiles (paint-order keeps the fill crisp on top of the stroke).
const StrokeText = { WebkitTextStroke: '0.7px rgba(0,0,0,0.65)', paintOrder: 'stroke fill' }

const RANGES = ['1D', '5D', '1M', '6M', '1Y']
// ALL scope hidden for now — the whole-universe treemap is too dense to read.
// Filter logic + the 'all' branch stay intact; re-add this row to restore it.
const SCOPES = [
  { id: 'main', label: 'MAIN' },
  { id: 'gems', label: 'GEMS' },
]
const HEADER_H = 11
const GAP = 1
const DRILL_GAP = 1
// Shared morph timing for the zoom in/out of a sector sub-map.
const MORPH = { type: 'spring', stiffness: 320, damping: 34, mass: 0.8 }

// Abbreviate the sector names that otherwise truncate mid-word in the narrow
// phone header strips ("SEMICONDUCTOR EQUIPMENT…"). Names that already fit are
// returned as-is. Full names stay in stocks.sector (and the L2 headline).
const SECTOR_SHORT = {
  'Cloud & Data Centers': 'Cloud & DC',
  'Semiconductor Equipment & Materials': 'Semi Equip',
  'Networking & Connectivity': 'Networking',
  'Quantum Computing': 'Quantum',
  'Memory & Storage': 'Memory',
  'Healthcare': 'Health',
}
export const shortSector = (s) => SECTOR_SHORT[s] ?? s

/* ── sector sub-theme copy for the filtered header (carried over from the
   retired Explore accordion headers). Keys are short display names; MKT_CANON
   bridges the full DB sector labels. ── */
const MKT_DESC = {
  'Compute': 'Edge inference · AI accelerators',
  'Photonics': 'Specialty optics · photonic chips',
  'Networking': 'Satellite · AR/VR · metro fiber',
  'Memory': 'MRAM · computational storage',
  'Semi Equip': 'Fab tools · critical minerals',
  'Data Centers': 'Colocation · miners going AI',
  'Quantum': 'Post-quantum · pure-play quantum',
  'Defense': 'eVTOL · drones · detection',
  'Healthcare': 'AI imaging · robotic surgery',
  'Energy & Grid': 'Nuclear · reactors · isotopes',
  'Power & Cooling': 'Fuel cells · grid batteries',
  'Cybersecurity': 'IoT security · post-quantum',
}
const MKT_CANON = {
  'Cloud & Data Centers': 'Data Centers',
  'Semiconductor Equipment & Materials': 'Semi Equip',
  'Networking & Connectivity': 'Networking',
  'Memory & Storage': 'Memory',
  'Quantum Computing': 'Quantum',
}
const canonSector = (s) => (s && MKT_CANON[s]) || s || null

function fmtPct(v) {
  if (v == null) return '—'
  const a = Math.abs(v)
  return `${v >= 0 ? '+' : '−'}${a >= 10 ? a.toFixed(0) : a.toFixed(1)}%`
}

function useContainerSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 })
  useEffect(() => {
    if (!ref.current) return
    const el = ref.current
    const update = () => { const r = el.getBoundingClientRect(); setSize({ w: r.width, h: r.height }) }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return size
}

/* The squarified treemap — pure presentation now: it renders whatever `rows`
   the screen computed (already scope/sector-filtered, change attached), so the
   map and the list can never disagree. Sector bands sized by aggregate
   (sqrt-scaled) market cap, tickers within sized the same, colored by % move.
   drillSector / onDrill: Level 2 — tapping a sector region zooms into that one
   sector's stocks, morphing via a shared layoutId.
   Exported for the V3 desktop Markets desk (same map, no fork). */
// Reserved strip at the board's bottom edge for the color legend — tiles never
// lay out into it, so the legend can't paint over the smallest sector's tiles.
const LEGEND_H = 16

export function MarketTreemap({ rows, cfg, onTicker, drillSector, onDrill, loading }) {
  const containerRef = useRef(null)
  const { w, h: rawH } = useContainerSize(containerRef)
  const h = Math.max(0, rawH - LEGEND_H)

  const pricesReady = rows.some((r) => r.change != null)

  const sizeOf = (mcap) => Math.pow(Math.max(1, mcap), 0.25)
  const FALLBACK_MCAP = 500

  // LEVEL 1 — sector bands + tickers within each.
  const layout = useMemo(() => {
    if (!w || !h || !rows.length) return { sectors: [], tickers: [] }

    const groups = {}
    for (const r of rows) {
      const mcap = r.mcap > 0 ? r.mcap : FALLBACK_MCAP
      const sector = r.sector || 'Other'
      if (!groups[sector]) groups[sector] = { sector, sizeValue: 0, stocks: [] }
      groups[sector].stocks.push({ ticker: r.ticker, name: r.name, sizeValue: sizeOf(mcap), change: r.change, price: r.price })
    }
    for (const g of Object.values(groups)) g.sizeValue = g.stocks.reduce((a, st) => a + st.sizeValue, 0)

    const sectorList = Object.values(groups).filter((g) => g.sizeValue > 0).sort((a, b) => b.sizeValue - a.sizeValue)
    if (!sectorList.length) return { sectors: [], tickers: [] }

    const total = sectorList.reduce((a, g) => a + g.sizeValue, 0)
    const scale = (w * h) / total
    const placedSectors = squarify(sectorList.map((g) => ({ ...g, area: g.sizeValue * scale })), { x: 0, y: 0, w, h })

    const placedTickers = []
    for (const sec of placedSectors) {
      const innerX = sec.x + GAP, innerY = sec.y + HEADER_H + GAP
      const innerW = sec.w - GAP * 2, innerH = sec.h - HEADER_H - GAP * 2
      if (innerW <= 0 || innerH <= 0) continue
      const sorted = sec.stocks.slice().sort((a, b) => b.sizeValue - a.sizeValue)
      const innerTotal = sorted.reduce((a, t) => a + t.sizeValue, 0)
      if (innerTotal <= 0) continue
      const innerScale = (innerW * innerH) / innerTotal
      const placed = squarify(sorted.map((t) => ({ ...t, area: t.sizeValue * innerScale })), { x: innerX, y: innerY, w: innerW, h: innerH })
      for (const p of placed) placedTickers.push({ ...p, sector: sec.sector })
    }
    return { sectors: placedSectors, tickers: placedTickers }
  }, [rows, w, h])

  // LEVEL 2 — the drilled sector's stocks re-squarified across the full board.
  const drillLayout = useMemo(() => {
    if (!drillSector || !w || !h || !rows.length) return []
    const list = rows
      .filter((r) => (r.sector || 'Other') === drillSector)
      .map((r) => ({ ticker: r.ticker, name: r.name, sizeValue: sizeOf(r.mcap > 0 ? r.mcap : FALLBACK_MCAP), change: r.change, price: r.price }))
    if (!list.length) return []
    list.sort((a, b) => b.sizeValue - a.sizeValue)
    const total = list.reduce((a, t) => a + t.sizeValue, 0)
    const scale = (w * h) / total
    return squarify(list.map((t) => ({ ...t, area: t.sizeValue * scale })), { x: 0, y: 0, w, h })
  }, [drillSector, rows, w, h])

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', background: 'hsl(var(--studio-background))', overflow: 'hidden' }}>
      {loading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', ...Sans, fontSize: 12, color: 'hsl(var(--studio-text-subtle))' }}>Loading the universe…</div>
      )}
      {!loading && !pricesReady && (
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 6, ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))' }}>Loading prices…</div>
      )}

      {/* LEVEL 1 — headers (visual), ticker tiles (→ TickerSheet), and a
          transparent per-sector drill catcher beneath the tiles. */}
      {!drillSector && (
        <>
          {/* Sector drill catchers: full sector rect, below tiles, carry the
              shared layoutId so the tapped one morphs into the Level 2 board. */}
          {layout.sectors.map((s) => (
            <motion.button
              key={`drill-${s.sector}`}
              layoutId={`drill-${s.sector}`}
              transition={MORPH}
              onClick={() => onDrill(s.sector)}
              aria-label={`Zoom into ${s.sector}`}
              style={{
                position: 'absolute', left: s.x, top: s.y, width: s.w, height: s.h,
                background: 'transparent', border: 'none', padding: 0, margin: 0,
                cursor: 'zoom-in', zIndex: 1,
              }}
            />
          ))}

          {layout.sectors.map((s) => (
            <div key={`sec-${s.sector}`} title={s.sector} style={{
              // A band shorter than the header strip clips its own label (and
              // bleeds into the neighbor below) — cap the strip to the band and
              // drop the text; the drill catcher + tooltip still identify it.
              position: 'absolute', left: s.x, top: s.y, width: s.w, height: Math.min(HEADER_H, s.h),
              background: 'hsl(var(--studio-background))', color: 'hsl(var(--studio-text-muted))',
              ...Mono, fontSize: 8.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
              lineHeight: `${HEADER_H}px`, padding: '0 5px',
              overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
              borderBottom: '1px solid hsl(var(--studio-border) / 0.3)',
              pointerEvents: 'none', zIndex: 3,
            }}>{s.h >= HEADER_H + 4 ? shortSector(s.sector) : ''}</div>
          ))}

          {layout.tickers.map((t) => {
            const tw = Math.max(0, t.w - 1), th = Math.max(0, t.h - 1)
            if (tw < 2 || th < 2) return null
            const dim = Math.min(tw, th)
            // Size the symbol to actually FIT the tile width (JetBrains Mono
            // ~0.6em advance) so short and long tickers both render un-clipped,
            // then label every tile that can fit even a ~4.5px symbol. Blank
            // tiles read as more of a bug than a small ticker (Finviz labels
            // everything). Only the truly tiny slivers stay label-free.
            const fitFs = (tw - 2) / (Math.max(3, t.ticker.length) * 0.6)
            const tickerFs = Math.min(dim * 0.30, Math.max(5, fitFs))
            // Adaptive precision keeps the string short on small tiles: 2 dp on
            // big tiles, 1 dp medium, 0 dp tiny ("+4%").
            const dec = dim > 34 ? 2 : dim > 16 ? 1 : 0
            const pctStr = t.change == null ? '—' : `${t.change >= 0 ? '+' : ''}${t.change.toFixed(dec)}%`
            // The % font SHRINKS to fit the tile width (down to a 4px floor) so
            // small tiles still get a number instead of going blank — dynamic.
            const pctWidthFs = (tw - 1) / (pctStr.length * 0.6)
            const pctFs = Math.max(4, Math.min(13, dim * 0.15, pctWidthFs))
            const showTicker = th >= 8 && fitFs >= 4.5
            // Label whenever a >=4px % string fits the width AND there's vertical
            // room for the 2nd line. Only true slivers stay blank.
            const showPct = showTicker && pctWidthFs >= 4 && th >= tickerFs + pctFs + 1
            return (
              <button key={`t-${t.ticker}`} onClick={() => onTicker(t.ticker)}
                title={`${t.ticker} · ${t.name ?? ''}${t.change != null ? ` · ${t.change.toFixed(2)}%` : ''}`}
                style={{
                  position: 'absolute', left: t.x, top: t.y, width: tw, height: th, border: 'none',
                  background: colorForPct(t.change, cfg.bound), color: '#fff', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  ...Mono, overflow: 'hidden', lineHeight: 1.1, textAlign: 'center', padding: 0, zIndex: 2,
                }}>
                {showTicker && <span style={{ fontSize: tickerFs, fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.55)', ...StrokeText, maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{t.ticker}</span>}
                {showTicker && showPct && (
                  <span style={{ fontSize: pctFs, opacity: 0.92, textShadow: '0 1px 2px rgba(0,0,0,0.55)', ...StrokeText }}>
                    {pctStr}
                  </span>
                )}
              </button>
            )
          })}
        </>
      )}

      {/* color → performance legend — lives in the reserved LEGEND_H strip
          below the board, never over tiles (L1 or drilled L2) */}
      {!loading && pricesReady && (
        <div style={{ position: 'absolute', right: 8, bottom: 3, zIndex: 6, display: 'flex', alignItems: 'center', gap: 6, pointerEvents: 'none' }}>
          <span style={{ ...Mono, fontSize: 8, color: 'hsl(var(--studio-text-muted))', textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>−{cfg.bound}%</span>
          <span style={{ width: 64, height: 6, borderRadius: 3, background: `linear-gradient(90deg, ${colorForPct(-cfg.bound, cfg.bound)}, ${colorForPct(0, cfg.bound)}, ${colorForPct(cfg.bound, cfg.bound)})`, boxShadow: '0 1px 3px rgba(0,0,0,0.5)' }} />
          <span style={{ ...Mono, fontSize: 8, color: 'hsl(var(--studio-text-muted))', textShadow: '0 1px 2px rgba(0,0,0,0.7)' }}>+{cfg.bound}%</span>
        </div>
      )}

      {/* LEVEL 2 — the drilled sector, filling the board. Morphs from the
          tapped sector's Level 1 rect via the shared layoutId. */}
      <AnimatePresence>
        {drillSector && (
          <motion.div
            key={`l2-${drillSector}`}
            layoutId={`drill-${drillSector}`}
            transition={MORPH}
            style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'hsl(var(--studio-background))', overflow: 'hidden' }}
          >
            {drillLayout.map((t, i) => {
              const tw = Math.max(0, t.w - DRILL_GAP), th = Math.max(0, t.h - DRILL_GAP)
              if (tw < 2 || th < 2) return null
              const dim = Math.min(tw, th)
              const logoSize = Math.round(Math.max(14, Math.min(56, dim * 0.42)))
              const showLogo = dim > 34
              const tickerFs = Math.max(9, Math.min(30, dim * 0.2))
              const dec = dim > 40 ? 2 : dim > 22 ? 1 : 0
              const pctStr = t.change == null ? '—' : `${t.change >= 0 ? '+' : ''}${t.change.toFixed(dec)}%`
              // Dynamic: shrink the % to fit the tile width; label whenever a
              // >=6px string fits AND there's room for the 2nd line.
              const pctWidthFs = (tw - 1) / (pctStr.length * 0.6)
              const pctFs = Math.max(6, Math.min(18, dim * 0.13, pctWidthFs))
              const showPct = pctWidthFs >= 6 && th >= tickerFs + pctFs + 1
              return (
                <motion.button
                  key={`d-${t.ticker}`}
                  onClick={() => onTicker(t.ticker)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, delay: Math.min(0.18, i * 0.012) }}
                  title={`${t.ticker} · ${t.name ?? ''}${t.change != null ? ` · ${t.change.toFixed(2)}%` : ''}`}
                  style={{
                    position: 'absolute', left: t.x, top: t.y, width: tw, height: th, border: 'none',
                    background: colorForPct(t.change, cfg.bound), color: '#fff', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: showLogo ? Math.max(2, dim * 0.04) : 0,
                    ...Mono, overflow: 'hidden', lineHeight: 1.1, textAlign: 'center', padding: 0,
                  }}>
                  {showLogo && <StockLogo ticker={t.ticker} size={logoSize} />}
                  <span style={{ fontSize: tickerFs, fontWeight: 700, textShadow: '0 1px 2px rgba(0,0,0,0.55)', ...StrokeText, maxWidth: '100%', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{t.ticker}</span>
                  {showPct && (
                    <span style={{ fontSize: pctFs, opacity: 0.94, textShadow: '0 1px 2px rgba(0,0,0,0.55)', ...StrokeText }}>
                      {pctStr}
                    </span>
                  )}
                </motion.button>
              )
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ── Leaders & Laggards band cell — top gainer / top loser hero with a real
   candlestick chart, the Leverage/Global Desk idiom. Tap opens the sheet. ── */
function BandCell({ row, up, tf, onOpen }) {
  const c = up ? '--studio-success' : '--studio-danger'
  return (
    <button onClick={() => onOpen(row.ticker)}
      style={{ flex: 1, minWidth: 0, padding: '13px 14px', cursor: 'pointer', textAlign: 'left', background: 'transparent',
        border: 'none', borderLeft: up ? 'none' : '1px solid hsl(var(--studio-border))' }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: `hsl(var(${c}))`, display: 'flex', alignItems: 'center', gap: 5 }}>
        {up ? '▲' : '▼'} {up ? 'Top gainer' : 'Top loser'} · {tf}
      </div>
      <div style={{ ...Display, fontSize: 27, lineHeight: 1, color: `hsl(var(${c}))`, marginTop: 7 }}>{fmtPct(row.change)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 5, minWidth: 0 }}>
        <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-text))' }}>{row.ticker}</span>
        <span style={{ ...Sans, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.name}</span>
      </div>
      <div style={{ marginTop: 6 }}><HeroCandles ticker={row.ticker} range={tf} w={150} h={34} /></div>
    </button>
  )
}

/* ── one ranked list row — the Explore row shape: logo · ticker/name ·
   spark · price + %. GEM mark on hunting rows when browsing ALL. ── */
function UniverseRow({ s, idx, onTicker, showGem }) {
  const [hot, setHot] = useState(false)
  const up = (s.change ?? 0) >= 0
  const col = s.change == null ? '--studio-text-subtle' : up ? '--studio-success' : '--studio-danger'
  return (
    <div
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      onClick={() => onTicker(s.ticker)}
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.5)' : 'transparent',
        borderTop: idx ? '1px solid hsl(var(--studio-border) / 0.35)' : 'none',
        transition: 'background 140ms var(--studio-ease)' }}>
      <StockLogo ticker={s.ticker} size={30} rounded="50%" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...Mono, fontSize: 13.5, color: hot ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text))', transition: 'color 160ms' }}>{s.ticker}</span>
          {showGem && (
            <span style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 3, padding: '1px 3px', flexShrink: 0 }}>GEM</span>
          )}
        </div>
        <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150, marginTop: 2 }}>{s.name}</div>
      </div>
      <Spark data={s.spark} up={up} width={50} height={22} />
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 62 }}>
        <div style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))', fontVariantNumeric: 'tabular-nums' }}>{s.price != null && s.price > 0 ? `$${s.price.toFixed(2)}` : '—'}</div>
        <div style={{ ...Mono, fontSize: 11, marginTop: 3, color: `hsl(var(${col}))`, fontVariantNumeric: 'tabular-nums' }}>{fmtPct(s.change)}</div>
      </div>
    </div>
  )
}

// Maps a timeframe chip to the LIVE prices field for that window (snapshot
// exposes change1d; historical mode exposes change1w/1m/6m/1y — same mapping
// LeverageDesk/GlobalDesk already use for their live-historical fetches).
const LIVE_FIELD = { '1D': 'change1d', '5D': 'change1w', '1M': 'change1m', '6M': 'change6m', '1Y': 'change1y' }

const ROW_CAP = 50

function MarketsBody({ stocks, sectors, loading }) {
  const { openTicker } = useStockDetail()
  const [scope, setScope] = useState('main')   // open on main tier (ALL scope hidden — see SCOPES)
  const [tf, setTf] = useState('1D')
  const [sort, setSort] = useState('gain')   // 'gain' = biggest first (desc) | 'loss' = biggest drop first (asc)
  const [sector, setSector] = useState('all')
  const [view, setView] = useState('map')
  const [drillSector, setDrillSector] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const cfg = RANGE_CONFIG[tf] || RANGE_CONFIG['1D']

  // Live for every range now (progressively merged, 60-ticker batches):
  // snapshot mode for 1D, historical mode for 5D/1M/6M/1Y. Switching tf swaps
  // the fetch mode (useLivePrices keys its cache by mode::tickers), so each
  // range's numbers are the SAME live computation the ticker sheet uses.
  const tickers = useMemo(() => stocks.map((s) => s.ticker), [stocks])
  const { prices: live, loading: liveLoading } = useLivePrices(tickers, 60_000, tf === '1D' ? 'snapshot' : 'historical')
  const { updates } = useStockUpdates()
  // Windowed sparklines for the selected timeframe — ONE batched call over the
  // whole universe (price_history via /api/sparks?range), so every row gets the
  // correct shape with no row cap (audit F11). 1D returns a short recent-daily
  // trend; the visible 1D rows get a real intraday squiggle overlaid below.
  const { sparks } = useSparks(tf)

  const updatesByTicker = useMemo(() => {
    const m = new Map()
    for (const u of (updates || [])) {
      const cur = m.get(u.ticker)
      if (!cur || (u.updated_at && (!cur.updated_at || u.updated_at > cur.updated_at))) m.set(u.ticker, u)
    }
    return m
  }, [updates])

  // One derived pipeline — the band, the list, and the map all read `rows`.
  // Live value for the selected range, with the matching nightly stock_updates
  // column as the instant-paint fallback so rows render immediately and
  // upgrade as the live batches land (same pattern for every tf now, not just
  // 1D — see LIVE_FIELD / treemap.js's RANGE_CONFIG for the two field maps).
  const liveField = LIVE_FIELD[tf] || 'change1d'
  const rows = useMemo(() => {
    const out = []
    for (const s of stocks) {
      if (scope === 'main' && s.tier !== 'main') continue
      if (scope === 'gems' && s.tier !== 'hunting') continue
      if (sector !== 'all' && s.sector !== sector) continue
      const u = updatesByTicker.get(s.ticker)
      const lv = live[s.ticker]
      const change = typeof lv?.[liveField] === 'number' ? lv[liveField]
        : (typeof u?.[cfg.field] === 'number' ? u[cfg.field] : null)
      const price = typeof lv?.price === 'number' ? lv.price : typeof u?.price === 'number' ? u.price : null
      out.push({
        ticker: s.ticker, name: s.name || s.ticker, sector: s.sector || null, tier: s.tier,
        mcap: Number(s.market_cap_usd_m) || 0, change, price,
        nightly: typeof u?.[cfg.field] === 'number' ? u[cfg.field] : null,
      })
    }
    return out
  }, [stocks, scope, sector, liveField, cfg.field, live, updatesByTicker])

  // While the live fill for the selected range is in flight, rank by the
  // STABLE nightly close move instead of the half-merged live values —
  // otherwise every landing price batch reshuffles the list (and flips the
  // hero band, remounting its candles). One re-rank when the fill settles;
  // cells still colorize live.
  const settling = liveLoading
  const rankOf = (r) => (settling ? r.nightly : r.change)

  const sorted = useMemo(() => {
    const list = rows.slice()
    list.sort((a, b) => {
      const av = rankOf(a), bv = rankOf(b)
      if (av == null && bv == null) return a.ticker.localeCompare(b.ticker)
      if (av == null) return 1
      if (bv == null) return -1
      return sort === 'loss' ? av - bv : bv - av
    })
    return list
  }, [rows, sort, settling]) // eslint-disable-line react-hooks/exhaustive-deps

  // Leaders & Laggards = the highest + lowest move, independent of sort dir.
  let lead = null, lag = null
  for (const r of rows) {
    const v = rankOf(r)
    if (v == null) continue
    if (!lead || v > rankOf(lead)) lead = r
    if (!lag || v < rankOf(lag)) lag = r
  }
  if (lead && lead === lag) lag = null

  // Filter changes reset the row cap + any map drill (a drilled sector may not
  // exist under the new filter).
  const pickScope = (id) => { setScope(id); setExpanded(false); setDrillSector(null) }
  const pickSector = (v) => { setSector(v); setExpanded(false); setDrillSector(null) }
  const pickView = (v) => { setView(v); if (v === 'list') setDrillSector(null) }

  // Sector-filtered header flourish: blurb + a handful of member logos,
  // reshuffled when the filter changes (the retired Explore accordion idiom).
  const blurb = sector !== 'all' ? MKT_DESC[canonSector(sector)] : null
  const peek = useMemo(
    () => (sector === 'all' ? [] : rows.filter((r) => !LOGO_MISSING.has(r.ticker)).sort(() => Math.random() - 0.5).slice(0, 6)),
    [sector, rows.length], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const drilled = view === 'map' && drillSector
  const visible = expanded ? sorted : sorted.slice(0, ROW_CAP)

  // 1D wants an intraday shape that daily price_history can't express, so the
  // visible 1D rows get a real per-ticker intraday squiggle from chart-bars,
  // overlaid on top of the windowed sparks. Bounded to the on-screen rows
  // (ROW_CAP) so it can't fan out hundreds of requests; rows past the cap keep
  // the batched 1D recent-daily trend. Every OTHER range is fully covered by
  // the batched windowed sparks (no cap, no per-ticker fetch), so this stays
  // empty for them.
  const sparkTickers = useMemo(
    () => (tf === '1D' ? visible.slice(0, ROW_CAP).map((r) => r.ticker) : []),
    [visible, tf],
  )
  const { barsByTicker } = useTickerBars(sparkTickers, '1D')

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        {/* header */}
        <div style={{ flexShrink: 0, padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              {drilled ? (
                <button onClick={() => setDrillSector(null)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...Mono, fontSize: 9, letterSpacing: '0.18em', color: 'hsl(var(--studio-accent))', marginBottom: 3 }}>
                  <span style={{ fontSize: 13, lineHeight: 1 }}>‹</span>
                  <span>MARKETS</span>
                </button>
              ) : (
                <DeskSwitcher />
              )}
            </div>
            <SearchPill />
          </div>
          <div style={{ ...Display, fontSize: 26, color: 'hsl(var(--studio-text))', lineHeight: 1 }}>
            {drilled ? drillSector : sector !== 'all' ? shortSector(sector) : 'Markets'}
          </div>
          {drilled ? (
            <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-muted))', marginTop: 4 }}>
              {cfg.label} move · tap ‹ to return to the full map
            </div>
          ) : view === 'map' ? (
            <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-muted))', marginTop: 4 }}>
              Color = {cfg.label} move · size = market cap · tap a sector to zoom
            </div>
          ) : sector !== 'all' ? (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 6 }}>
              {peek.map((r, i) => (
                <span key={r.ticker} style={{ marginLeft: i === 0 ? 0 : -7, border: '1.5px solid hsl(var(--studio-background))', borderRadius: '50%', display: 'flex', background: 'hsl(var(--studio-background))', lineHeight: 0 }}>
                  <StockLogo ticker={r.ticker} size={20} rounded="50%" />
                </span>
              ))}
              <span style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', marginLeft: peek.length ? 9 : 0 }}>
                {blurb ? `${blurb} · ` : ''}{rows.length} names
              </span>
            </div>
          ) : (
            <div style={{ ...Sans, fontSize: 11, color: 'hsl(var(--studio-text-muted))', marginTop: 4 }}>
              Every Atlas name, hottest first · tap a row to open
            </div>
          )}

          {/* leaders & laggards — top gainer + top loser for the selected window (list view) */}
          {view === 'list' && (lead || lag) && (
            <div style={{ display: 'flex', marginTop: 14, background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', overflow: 'hidden' }}>
              {lead && <BandCell row={lead} up tf={tf} onOpen={openTicker} />}
              {lag && <BandCell row={lag} up={false} tf={tf} onOpen={openTicker} />}
            </div>
          )}

          {/* scope + view chips */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              {SCOPES.map((sc) => <Chip key={sc.id} active={scope === sc.id} onClick={() => pickScope(sc.id)}>{sc.label}</Chip>)}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <Chip active={view === 'list'} onClick={() => pickView('list')}>LIST</Chip>
              <Chip active={view === 'map'} onClick={() => pickView('map')}>MAP</Chip>
            </div>
          </div>
        </div>

        {/* column strip — sector filter left; timeframe + sort flip right
            (the flip only matters for the ranked list) */}
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px 7px' }}>
          <Dropdown value={sector} onChange={pickSector} align="left" size={8.5} fallbackLabel="All sectors" minWidth={170} maxHeight={320}
            options={[{ value: 'all', label: 'All sectors' }, ...sectors.map((s) => ({ value: s, label: s }))]} />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Dropdown value={tf} onChange={setTf} align="right" size={8.5} minWidth={140} options={RANGES.map((k) => ({ value: k, label: k }))} />
            {view === 'list' && (
              <button onClick={() => setSort((s) => (s === 'gain' ? 'loss' : 'gain'))} aria-label="Toggle sort direction"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'hsl(var(--studio-accent))', fontSize: 12, lineHeight: 1 }}>
                {sort === 'loss' ? '↑' : '↓'}
              </button>
            )}
          </span>
        </div>

        {/* body */}
        {view === 'list' ? (
          <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            {loading ? (
              [0, 1, 2, 3, 4, 5, 6, 7].map((i) => <SkeletonRow key={i} i={i} />)
            ) : sorted.length === 0 ? (
              <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>NO NAMES</div>
            ) : (
              <>
                {visible.map((s, i) => (
                  <UniverseRow key={s.ticker} s={{ ...s, spark: (tf === '1D' ? barsByTicker[s.ticker] : null) ?? sparks[s.ticker] ?? null }} idx={i} onTicker={openTicker} showGem={scope === 'all' && s.tier === 'hunting'} />
                ))}
                {!expanded && sorted.length > ROW_CAP && (
                  <button onClick={() => setExpanded(true)} style={{
                    width: '100%', padding: '15px 20px', background: 'transparent', border: 'none',
                    borderTop: '1px solid hsl(var(--studio-border) / 0.35)', cursor: 'pointer',
                    ...Mono, fontSize: 10, letterSpacing: '0.12em', color: 'hsl(var(--studio-accent))',
                  }}>SHOW ALL {sorted.length} NAMES</button>
                )}
              </>
            )}
            <div style={{ height: 14 }} />
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, margin: '4px 12px 8px', borderRadius: 'var(--studio-radius)', overflow: 'hidden', border: '1px solid hsl(var(--studio-border) / 0.5)' }}>
            <MarketTreemap rows={rows} cfg={cfg} onTicker={openTicker} drillSector={drillSector} onDrill={setDrillSector} loading={loading} />
          </div>
        )}

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

/* Fetch the roster once here so the hunting rows double as the TickerSheet
   `extraStocks` — without them openTicker() no-ops for gems (the provider's
   own roster is active-only; same pattern as GlobalDeskData). */
function MarketsData() {
  const { stocks, sectors, loading } = useAtlasStocks({ includeInactive: true })
  const extraStocks = useMemo(() => stocks.filter((s) => s.tier === 'hunting'), [stocks])
  return (
    <StockDetailProvider extraStocks={extraStocks}>
      <MarketsBody stocks={stocks} sectors={sectors} loading={loading} />
    </StockDetailProvider>
  )
}

export function Markets() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <MarketsData />
    </div>
  )
}

export default Markets
